import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';

export const ProductGallery = ({ galleryData, selectedColor = '#9333ea' }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);

  if (!galleryData) return null;
  
  const galleryItems = galleryData.items || [];

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback(
    (index) => {
      if (emblaApi) emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi, onSelect]);

  return (
    <div className="relative">
      {/* Glow effects */}
      <div className="absolute -inset-8 bg-green-500/10 rounded-full blur-3xl"></div>
      <div className="absolute -inset-8 bg-orange-500/10 rounded-full blur-3xl"></div>

      {/* Container for gallery and angled square */}
      <div className="relative">
        {/* Angled grey square - centered behind slideshow */}
        <div
          className="absolute rounded-2xl transform rotate-6 transition-all duration-500"
          style={{
            backgroundColor: 'rgb(194, 194, 194)',
            filter: 'blur(4px)',
            opacity: 0.8,
            zIndex: -1,
            width: '95%',
            height: '100%',
            top: '0%',
            left: '3%',
            transform: 'rotate(6deg)',
          }}
        ></div>

        {/* Main gallery */}
        <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {galleryItems.map((item, index) => (
                <div
                  key={index}
                  className="flex-[0_0_100%] min-w-0"
                >
                  <div className="w-full aspect-[3/4] bg-gray-700 flex items-center justify-center relative">
                    {item.type === 'image' ? (
                      item.url ? (
                        <img
                          src={item.url}
                          alt={item.alt || 'Gallery image'}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="text-center p-8">
                          <div className="w-16 h-16 bg-gray-600 rounded-lg mx-auto mb-4"></div>
                          <p className="gaming-text text-gray-500 text-sm uppercase">No image</p>
                        </div>
                      )
                    ) : (
                      item.url ? (
                        <video
                          src={item.url}
                          autoPlay
                          loop
                          muted
                          playsInline
                          className="w-full h-full object-cover"
                        >
                          Your browser does not support the video tag.
                        </video>
                      ) : (
                        <div className="text-center p-8">
                          <div className="w-16 h-16 bg-gray-600 rounded-lg mx-auto mb-4 flex items-center justify-center">
                            <Play className="w-8 h-8 text-gray-500" />
                          </div>
                          <p className="gaming-text text-gray-500 text-sm uppercase">{item.alt}</p>
                          <p className="gaming-text text-gray-600 text-xs mt-2">Replace with your video</p>
                        </div>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Buttons */}
          <button
            onClick={scrollPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/70 hover:bg-black/90 rounded-full flex items-center justify-center transition-all duration-300 border border-orange-500/50 hover:border-orange-500"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          <button
            onClick={scrollNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/70 hover:bg-black/90 rounded-full flex items-center justify-center transition-all duration-300 border border-orange-500/50 hover:border-orange-500"
            aria-label="Next slide"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="flex justify-center space-x-2 mt-4">
        {galleryItems.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollTo(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === selectedIndex
                ? 'bg-orange-500 w-8'
                : 'bg-gray-600 hover:bg-gray-500'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};
